'use client';

import { MCPSettings } from '../../types/ai-settings';

interface MCPConfigurationProps {
  settings?: MCPSettings;
  onChange: (settings: MCPSettings) => void;
}

export function MCPConfiguration({ settings, onChange }: MCPConfigurationProps) {
  const currentSettings: MCPSettings = settings || {
    enabled: false,
    transport: 'http',
  };

  const handleChange = <K extends keyof MCPSettings>(
    key: K,
    value: MCPSettings[K]
  ) => {
    onChange({ ...currentSettings, [key]: value });
  };

  const handleReconnectChange = (
    key: keyof NonNullable<MCPSettings['reconnect']>,
    value: any
  ) => {
    onChange({
      ...currentSettings,
      reconnect: {
        enabled: true,
        maxAttempts: 5,
        delay: 1000,
        ...currentSettings.reconnect,
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* MCP Overview */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
          <span>ℹ️</span>
          About Model Context Protocol
        </h3>
        <p className="text-xs text-white/70 leading-relaxed">
          The Model Context Protocol (MCP) is an open standard for communication between AI applications and external services. When enabled, Vibecode can connect to MCP-compatible servers to extend its capabilities with additional tools and data sources.
        </p>
        <a
          href="https://modelcontextprotocol.io/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          Learn more about MCP →
        </a>
      </div>

      {/* Enable MCP */}
      <div className="bg-white/5 rounded-lg p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={currentSettings.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
            className="w-4 h-4 bg-white/10 border border-white/20 rounded text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
          />
          <div>
            <span className="text-sm font-medium text-white">Enable MCP Integration</span>
            <p className="text-xs text-white/60 mt-0.5">
              Connect to MCP servers for enhanced capabilities
            </p>
          </div>
        </label>
      </div>

      {/* MCP Configuration (only show when enabled) */}
      {currentSettings.enabled && (
        <>
          {/* Transport Configuration */}
          <div className="bg-white/5 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <span>🔌</span>
              Transport Configuration
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-white/70 mb-2">
                  Transport Type
                </label>
                <select
                  value={currentSettings.transport}
                  onChange={(e) => handleChange('transport', e.target.value as any)}
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
                >
                  <option value="stdio">Standard I/O</option>
                  <option value="http">HTTP</option>
                  <option value="websocket">WebSocket</option>
                </select>
                <p className="text-xs text-white/40 mt-1">
                  {currentSettings.transport === 'stdio' && 'Communicate with local MCP servers via stdin/stdout'}
                  {currentSettings.transport === 'http' && 'Connect to MCP servers over HTTP/HTTPS'}
                  {currentSettings.transport === 'websocket' && 'Real-time bidirectional communication via WebSocket'}
                </p>
              </div>

              {(currentSettings.transport === 'http' || currentSettings.transport === 'websocket') && (
                <>
                  <div>
                    <label className="block text-xs text-white/70 mb-2">
                      Server Endpoint
                    </label>
                    <input
                      type="text"
                      value={currentSettings.endpoint || ''}
                      onChange={(e) => handleChange('endpoint', e.target.value || undefined)}
                      placeholder={
                        currentSettings.transport === 'http'
                          ? 'https://mcp-server.example.com/api'
                          : 'wss://mcp-server.example.com/ws'
                      }
                      className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-white/70 mb-2">
                      Custom Headers (JSON)
                    </label>
                    <textarea
                      value={currentSettings.headers ? JSON.stringify(currentSettings.headers, null, 2) : ''}
                      onChange={(e) => {
                        try {
                          const headers = e.target.value ? JSON.parse(e.target.value) : undefined;
                          handleChange('headers', headers);
                        } catch {
                          // Invalid JSON, don't update
                        }
                      }}
                      placeholder='{\n  "Authorization": "Bearer token",\n  "X-Custom-Header": "value"\n}'
                      rows={4}
                      className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none resize-none"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Reconnection Settings */}
          {currentSettings.transport === 'websocket' && (
            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                <span>🔄</span>
                Reconnection Settings
              </h3>

              <div className="space-y-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={currentSettings.reconnect?.enabled ?? false}
                    onChange={(e) => handleReconnectChange('enabled', e.target.checked)}
                    className="w-4 h-4 bg-white/10 border border-white/20 rounded text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
                  />
                  <span className="text-sm text-white">Enable Auto-reconnect</span>
                </label>

                {currentSettings.reconnect?.enabled && (
                  <>
                    <div>
                      <label className="block text-xs text-white/70 mb-2">
                        Max Reconnection Attempts
                      </label>
                      <input
                        type="number"
                        value={currentSettings.reconnect?.maxAttempts ?? 5}
                        onChange={(e) => handleReconnectChange('maxAttempts', parseInt(e.target.value) || 5)}
                        min="1"
                        max="100"
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-white/70 mb-2">
                        Reconnection Delay (ms)
                      </label>
                      <input
                        type="number"
                        value={currentSettings.reconnect?.delay ?? 1000}
                        onChange={(e) => handleReconnectChange('delay', parseInt(e.target.value) || 1000)}
                        min="100"
                        max="60000"
                        step="100"
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* MCP Server Status */}
          <div className="bg-white/5 rounded-lg p-4">
            <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
              <span>📊</span>
              MCP Server Status
            </h3>

            <div className="text-center py-8 text-white/40">
              <p className="text-sm mb-2">MCP server connection not active</p>
              <p className="text-xs">
                Configure and save settings to establish connection
              </p>
            </div>
          </div>
        </>
      )}

      {/* Future Features */}
      <div className="bg-white/5 rounded-lg p-4">
        <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <span>🚀</span>
          Coming Soon
        </h3>
        <ul className="space-y-2 text-xs text-white/60">
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Automatic MCP server discovery</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Built-in MCP server for local tools</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>MCP marketplace integration</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span>Custom tool development SDK</span>
          </li>
        </ul>
      </div>
    </div>
  );
}